class ProductForm extends HTMLElement {
  #abortController;

  constructor() {
    super();
    this.#abortController = new AbortController();
  }

  connectedCallback() {
    const form = this.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    

    form.addEventListener("submit", this.handleSubmit.bind(this), {
      signal: this.#abortController.signal,
    });

    form.id.disabled = false;
  }

  // Main submit handler: routes logic based on hw card conditions
  async handleSubmit(event) {
    event.preventDefault();
    
    const form = this.querySelector('form[action*="/cart/add"]');

    if (this.isBusy(event) || !form.checkValidity()) {
      form.reportValidity?.();
      return;
    }

    const showLoading = this.usesLoadingBar(event);
    const submitButtons = this.setLoadingState(form, true);

    const formData = new FormData(form);
    this.prepareSections(formData);

    const hw = this.extractHwData(event, formData);

    let responseJson;
    if (showLoading) this.startLoading();

    if (hw.shouldBundle) {
      responseJson = await this.addBundledItems(hw, formData);
    } else {
      responseJson = await this.addStandardItem(formData);
    }

    this.setLoadingState({ elements: submitButtons }, false);
    if (showLoading) this.stopLoading();

    await this.processResponse(form, responseJson);
  }

  // ---- Helper Logic ---- //

  isBusy(event) {
    return event.submitter?.getAttribute("aria-busy") === "true";
  }

  usesLoadingBar(event) {
    return event.submitter?.querySelector("button-content") === null;
  }

  setLoadingState(form, isBusy) {
    const buttons = Array.from(form.elements).filter(
      (el) => el.type === "submit"
    );
    buttons.forEach((btn) =>
      isBusy ? btn.setAttribute("aria-busy", "true") : btn.removeAttribute("aria-busy")
    );
    return buttons;
  }

  startLoading() {
    document.documentElement.dispatchEvent(
      new CustomEvent("theme:loading:start", { bubbles: true })
    );
  }

  stopLoading() {
    document.documentElement.dispatchEvent(
      new CustomEvent("theme:loading:end", { bubbles: true })
    );
  }

  prepareSections(formData) {
    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );
    formData.set("sections", [...sectionsToBundle].join(","));
  }

  extractHwData(event, formData) {
    const hwCardAdded = formData.get("properties[_hw_card_added]") === "on";
    const hwCardVariantId = formData.get("properties[_hw_card_variant_id]");

    const propertySelector = event?.target
      ?.closest(".product-info__block-list")
      ?.querySelector("auto-checker")
      ?.getAttribute("data-hw-input-property-name");

    const hwInputPropertyName =
      propertySelector?.match(/properties\[(.+)\]/)?.[1] || propertySelector;

    const hwInputValue = (formData.get(propertySelector) || "").trim();

    if (!hwCardAdded) formData.delete(propertySelector);
    if (!hwInputValue) formData.delete("properties[_hw_card_added]");

    const shouldBundle =
      hwCardAdded && hwCardVariantId && hwInputValue && hwInputPropertyName;

    return {
      shouldBundle,
      hwCardVariantId,
      hwInputPropertyName,
      hwInputValue,
      groupId: Date.now().toString(),
    };
  }

  async addBundledItems(hw, formData) {
    const payload = {
      items: [
        {
          id: Number(hw.hwCardVariantId),
          quantity: 1,
          properties: { _hw_group_id: hw.groupId },
        },
        {
          id: Number(formData.get("id")),
          quantity: 1,
          properties: {
            _hw_group_id: hw.groupId,
            [hw.hwInputPropertyName]: hw.hwInputValue,
          },
        },
      ],
      sections: formData.get("sections"),
    };

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    return await response.json();
  }

  async addStandardItem(formData) {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: "POST",
      body: formData,
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    return await response.json();
  }

  async processResponse(form, responseJson) {
    const response = responseJson;

    if (!response) {
      console.warn('No response received');
      return;
    }

    if (response.items || response.id) {
      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      document.dispatchEvent(
        new CustomEvent("variant:add", {
          bubbles: true,
          detail: { items: response.items || [response], cart: cartContent },
        })
      );

      document.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: { 
            cart: cartContent,
            baseEvent: "variant:add"
          },
        })
      );
    } else {
      document.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: { error: responseJson.message },
        })
      );
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
